import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './styles/admin.scss';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import ConfirmModal from '../../components/ConfirmModal';
import { PRODUCTS, PRODUCT_LABEL, ATTEMPT_MODE, SUBJECT_LABEL } from './adminConstants';

type Entitlement = { id: string; product: number; validFrom: string; validTo?: string | null };
type Ref = { id: string; name: string };
type Attempt = { id: string; mode: number; subject?: number | null; scorePercent: number; correctQuestions: number; totalQuestions: number; createdAt: string };
type Detail = {
    id: string; name?: string; surname?: string; email: string; confirmed: boolean;
    facultyId?: string | null; facultyName?: string | null;
    entitlements: Entitlement[]; groups: Ref[]; credits: number;
    completedLessons: number; recentAttempts: Attempt[];
};

function fmt(d?: string | null) { if (!d) return "—"; const x = new Date(d); return x.getDate() + ". " + (x.getMonth() + 1) + ". " + x.getFullYear(); }

export default function AdminUserDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const api = new ApiHelper();

    const [d, setD] = useState<Detail | null>(null);
    const [faculties, setFaculties] = useState<{ id: string; name: string }[]>([]);
    const [groupOptions, setGroupOptions] = useState<Ref[]>([]);
    const [pw, setPw] = useState("");
    const [grant, setGrant] = useState({ product: 1, validFrom: "", validTo: "" });
    const [addGroupId, setAddGroupId] = useState("");
    const [confirmDel, setConfirmDel] = useState(false);

    useEffect(() => { load(); loadOptions(); }, [id]);

    async function load() {
        try { setD(await (await api.adminUsersApi.detail(id!)).json()); } catch (x: any) { api.adminError(x); }
    }
    async function loadOptions() {
        try { setFaculties(await (await api.facultiesApi.getFacultiesList()).json()); } catch { }
        try { setGroupOptions(await (await api.adminUsersApi.groupOptions()).json()); } catch (x: any) { api.adminError(x); }
    }

    async function run(p: Promise<any>, ok: string): Promise<boolean> {
        try { await p; api.alert.success(ok); await load(); return true; } catch (x: any) { api.adminError(x); return false; }
    }

    if (!d) return <div><span className="backLink" onClick={() => navigate("/admin/studenti")}>← Studenti</span></div>;

    return (
        <div>
            <span className="backLink" onClick={() => navigate("/admin/studenti")}>← Studenti</span>
            <div className="adminHead">
                <div>
                    <h1>{(d.name || "") + " " + (d.surname || "") || d.email}</h1>
                    <p>{d.email} · {d.completedLessons} dokončených lekcí</p>
                </div>
                <button className="aBtn danger" onClick={() => setConfirmDel(true)}>Smazat účet</button>
            </div>

            <div className="aGrid">
                {/* Profile */}
                <div className="aCard">
                    <h2>Profil</h2>
                    <div className="aField"><label>Jméno</label><input value={d.name ?? ""} onChange={e => setD({ ...d, name: e.target.value })} /></div>
                    <div className="aField"><label>Příjmení</label><input value={d.surname ?? ""} onChange={e => setD({ ...d, surname: e.target.value })} /></div>
                    <div className="aField"><label>E-mail</label><input type="email" value={d.email} onChange={e => setD({ ...d, email: e.target.value })} /></div>
                    <div className="aField">
                        <label className="toggle"><input type="checkbox" checked={d.confirmed} onChange={e => setD({ ...d, confirmed: e.target.checked })} /><i className="slider" /> E-mail ověřen</label>
                    </div>
                    <div className="aActions">
                        <button className="aBtn" onClick={() => run(api.adminUsersApi.profile(d.id, { name: d.name, surname: d.surname, email: d.email, confirmed: d.confirmed }), "Profil uložen")}>Uložit profil</button>
                    </div>
                </div>

                {/* Password */}
                <div className="aCard">
                    <h2>Heslo</h2>
                    <div className="aField"><label>Nové heslo</label><input type="text" value={pw} onChange={e => setPw(e.target.value)} placeholder="min. 6 znaků" /></div>
                    <div className="aActions">
                        <button className="aBtn" disabled={pw.length < 6} onClick={() => run(api.adminUsersApi.password(d.id, { password: pw }), "Heslo změněno").then(ok => { if (ok) setPw(""); })}>Nastavit heslo</button>
                    </div>
                </div>

                {/* Access / products */}
                <div className="aCard">
                    <h2>Přístup / produkty</h2>
                    <div className="rowList">
                        {d.entitlements.length ? d.entitlements.map(e => (
                            <div key={e.id} className="lineItem">
                                <span><b>{PRODUCT_LABEL[e.product]}</b> · od {fmt(e.validFrom)}{e.validTo ? " do " + fmt(e.validTo) : ""}</span>
                                <span className="x" title="Odebrat" onClick={() => run(api.adminUsersApi.revoke(e.id), "Přístup odebrán")}>×</span>
                            </div>
                        )) : <span className="empty">Žádný aktivní přístup.</span>}
                    </div>
                    <div className="inlineRow">
                        <div className="aField inline"><label>Produkt</label><select value={grant.product} onChange={e => setGrant({ ...grant, product: Number(e.target.value) })}>{PRODUCTS.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}</select></div>
                        <div className="aField inline"><label>Platí od</label><input type="date" value={grant.validFrom} onChange={e => setGrant({ ...grant, validFrom: e.target.value })} /></div>
                        <div className="aField inline"><label>Platí do</label><input type="date" value={grant.validTo} onChange={e => setGrant({ ...grant, validTo: e.target.value })} /></div>
                        <button className="aBtn" onClick={() => run(api.adminUsersApi.grant(d.id, { product: grant.product, validFrom: grant.validFrom || undefined, validTo: grant.validTo || undefined }), "Přístup udělen")}>Udělit</button>
                    </div>
                </div>

                {/* Goal */}
                <div className="aCard">
                    <h2>Cíl (fakulta)</h2>
                    <div className="aField"><label>Cílová fakulta</label>
                        <select value={d.facultyId ?? ""} onChange={e => setD({ ...d, facultyId: e.target.value || null })}>
                            <option value="">— bez cíle —</option>
                            {faculties.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>
                    </div>
                    <div className="aActions"><button className="aBtn" onClick={() => run(api.adminUsersApi.goal(d.id, { facultyId: d.facultyId ?? null }), "Cíl uložen")}>Uložit cíl</button></div>
                </div>

                {/* Groups */}
                <div className="aCard">
                    <h2>Skupiny</h2>
                    <div className="rowList">
                        {d.groups.length ? d.groups.map(g => (
                            <div key={g.id} className="lineItem"><span>{g.name}</span><span className="x" title="Odebrat" onClick={() => run(api.adminUsersApi.removeGroup(d.id, g.id), "Odebráno ze skupiny")}>×</span></div>
                        )) : <span className="empty">Není v žádné skupině.</span>}
                    </div>
                    <div className="inlineRow">
                        <div className="aField inline"><label>Přidat do skupiny</label>
                            <select value={addGroupId} onChange={e => setAddGroupId(e.target.value)}>
                                <option value="">— vyber skupinu —</option>
                                {groupOptions.filter(g => !d.groups.some(x => x.id === g.id)).map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                            </select>
                        </div>
                        <button className="aBtn" disabled={!addGroupId} onClick={() => run(api.adminUsersApi.addGroup(d.id, { groupId: addGroupId }), "Přidáno do skupiny").then(ok => { if (ok) setAddGroupId(""); })}>Přidat</button>
                    </div>
                </div>

                {/* Credits */}
                <div className="aCard">
                    <h2>Kredity na lekce</h2>
                    <div className="aField"><label>Zůstatek</label><input type="number" min={0} value={d.credits} onChange={e => setD({ ...d, credits: Math.max(0, Number(e.target.value)) })} /></div>
                    <div className="aActions"><button className="aBtn" onClick={() => run(api.adminUsersApi.credits(d.id, { balance: d.credits }), "Kredity uloženy")}>Uložit kredity</button></div>
                </div>

                {/* Insights */}
                <div className="aCard">
                    <h2>Poslední pokusy</h2>
                    <div className="rowList">
                        {d.recentAttempts.length ? d.recentAttempts.map(a => (
                            <div key={a.id} className="lineItem">
                                <span>{ATTEMPT_MODE[a.mode]}{a.subject != null ? " · " + SUBJECT_LABEL[a.subject] : ""} · {fmt(a.createdAt)}</span>
                                <span><b>{a.correctQuestions}/{a.totalQuestions}</b> ({a.scorePercent} %)</span>
                            </div>
                        )) : <span className="empty">Zatím žádné pokusy.</span>}
                    </div>
                </div>
            </div>

            <ConfirmModal
                open={confirmDel}
                title="Smazat účet?"
                message={"Účet " + d.email + " a všechna jeho data (přístupy, pokusy, kredity, chat) budou nevratně smazána."}
                confirmLabel="Smazat"
                cancelLabel="Zrušit"
                onConfirm={() => { setConfirmDel(false); run(api.adminUsersApi.remove(d.id), "Účet smazán").then(ok => { if (ok) navigate("/admin/studenti"); }); }}
                onClose={() => setConfirmDel(false)}
            />
        </div>
    );
}
